"use client";

import { buildCategoryNavigationPath } from "@faire/deprecated-retailer-visitor-shared/categories/navigationPath";
import { FavoriteButton } from "@faire/deprecated-retailer-visitor-shared/components/Favorite/FavoriteButton";
import { INamedImage } from "@faire/deprecated-retailer-visitor-shared/components/ImageCarousel/ImageCarousel";
import { BrandTileBadge } from "@faire/deprecated-retailer-visitor-shared/components/LeanBrandTileRenderer/__internal__/BrandTileBadge";
import { BrandTileImageSection } from "@faire/deprecated-retailer-visitor-shared/components/LeanBrandTileRenderer/__internal__/BrandTileImageSection";
import { LeanBrandTileStyles } from "@faire/deprecated-retailer-visitor-shared/components/LeanBrandTileRenderer/__internal__/LeanBrandTileStyles";
import { getPromoBannerStyle } from "@faire/deprecated-retailer-visitor-shared/features/Events/utils";
import {
  followBrand,
  unFollowBrand,
} from "@faire/deprecated-retailer-visitor-shared/features/RetailerBoards/utils";
import { useIsLoggedInRetailer } from "@faire/deprecated-retailer-visitor-shared/lib/isLoggedInRetailer";
import {
  popSignUp,
  SignUpType,
} from "@faire/deprecated-retailer-visitor-shared/lib/popSignUp";
import {
  brandPage,
  isFromCategoryURL,
  isFromDiscoverURL,
} from "@faire/deprecated-retailer-visitor-shared/paths/index";
import { BrandPageQueryObj } from "@faire/deprecated-retailer-visitor-shared/search/BrandPageQueryObj";
import { getRetailer } from "@faire/deprecated-retailer-visitor-shared/serialized-data/getRetailer";
import { isNewCategory } from "@faire/deprecated-retailer-visitor-shared/services/Categories";
import { useMarketStore } from "@faire/deprecated-retailer-visitor-shared/stores/domain/Events/FaireMarkets/MarketStore";
import { trackRetailerPortalCategoryPageBrandTileClick } from "@faire/web-api--source/events/retailerPortal/click/categoryPageBrandTile";
import { trackRetailerPortalDiscoverPageBrandTileClick } from "@faire/web-api--source/events/retailerPortal/click/discoverPageBrandTile";
import { trackSeoCategoryBrandListBrandLinkRefsClick } from "@faire/web-api--source/events/seo/click/categoryBrandListBrandLinkRefs";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { getLocationOrThrow } from "@faire/web/common/globals/getLocation";
import { optimizeImageUrl } from "@faire/web/common/images/optimizeImages";
import { useStrictLocalization } from "@faire/web/common/localization";
import { DEPRECATED_backwardsCompatibleValues } from "@faire/web/common/localization/localize";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { trackEvent } from "@faire/web/common/trackEvent";
import { LoadingSkeleton } from "@faire/web/slate-deprecated/LoadingSkeleton";
import { Column } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import pickBy from "lodash/pickBy";
import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

import { useBrandTileClickImpression } from "../useBrandTileClickImpression";
import { useBrandTileViewImpression } from "../useBrandTileViewImpression";

import { BrandTileProps } from "./BrandTileProps";

export const LeanBrandTileRendererBase: React.FC<BrandTileProps> = ({
  brandTile,
  categoryUrlIdList,
  filterKeys,
  favoriteBrand,
  unFavoriteBrand,
  className,
  brandPageQueryParams,
  eventId,
  onClick,
  title,
  pageType,
  customButton,
  withoutFavourite,
  transparentFeaturedPromoTextBackground,
  bottomPadding,
  topPadding,
  showMinimum,
  signUpTracking,
  previousOrders,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const retailerToken = getRetailer()?.token;
  const [internalFavorite, setInternalFavorite] = useState(
    !!brandTile?.brand?.is_following
  );
  const { isLive, showAllSpecials } = useMarketStore();

  const isSSR = useIsSSR();
  const isLoggedInRetailer = useIsLoggedInRetailer();
  const history = useHistory();

  const viewImpressionProps = useBrandTileViewImpression({
    brandToken: brandTile?.brand?.token,
    impressionId: brandTile?.brand?.impression_id,
  });

  const { onLeftClick, onMiddleClick, onRightClick } =
    useBrandTileClickImpression({
      brandToken: brandTile?.brand?.token,
      impressionId: brandTile?.brand?.impression_id,
    });

  const brand = brandTile?.brand;
  const isLoaded = !!brand;

  const extendQueryParams = useCallback(
    (baseParams: BrandPageQueryObj | undefined) => {
      if (!categoryUrlIdList) {
        return baseParams ?? {};
      }
      // logged out users should not see ref parameters (SEO requirement)
      const param = isLoggedInRetailer
        ? {
            refC: buildCategoryNavigationPath(categoryUrlIdList),
            refF: filterKeys,
            sortingHint: true,
          }
        : {};
      return {
        ...pickBy({
          ...param,
        }),
        ...(baseParams ?? {}),
      };
    },
    [categoryUrlIdList, filterKeys, isLoggedInRetailer]
  );

  const queryParams = useMemo(() => {
    if (brandPageQueryParams === undefined) {
      return extendQueryParams({});
    }
    return brandPageQueryParams(extendQueryParams);
  }, [brandPageQueryParams, extendQueryParams]);

  const link = useMemo(() => {
    return brand?.token && brandPage(brand.token, queryParams);
  }, [brand?.token, queryParams]);

  const carouselImages = useMemo((): INamedImage[] => {
    if (!brandTile?.brand) {
      return [];
    }

    return brandTile.images.map((image: IImage) => ({
      ...image,
      url: optimizeImageUrl(image, isSSR, {
        crop: { smartSquare: true },
        ignoreResizeTag: true,
      }),
      name: image.alt_text,
    }));
  }, [brandTile?.brand, brandTile?.images, isSSR]);

  const onLinkClick = useCallback(() => {
    onLeftClick?.();
    const showReviews = !isLoggedInRetailer;

    onClick?.(brand?.token);
    const tileType = showReviews ? "REVIEW" : "BRAND_STORY";
    if (isFromDiscoverURL(getLocationOrThrow().pathname)) {
      trackRetailerPortalDiscoverPageBrandTileClick(tileType);
    } else if (isFromCategoryURL(getLocationOrThrow().pathname)) {
      trackRetailerPortalCategoryPageBrandTileClick(tileType);
    }

    if (!eventId) {
      return;
    }
    trackEvent(`/maker-tile/link/${eventId}`);
    const [c1UrlId, ...subCategoryUrlIds] = categoryUrlIdList ?? [];
    trackSeoCategoryBrandListBrandLinkRefsClick(
      brand?.token ?? "",
      c1UrlId ?? "",
      buildCategoryNavigationPath(subCategoryUrlIds) ?? ""
    );
  }, [
    brand?.token,
    categoryUrlIdList,
    eventId,
    onClick,
    isLoggedInRetailer,
    onLeftClick,
  ]);

  const { lightBackground, promoText } = getPromoBannerStyle(
    isLive,
    showAllSpecials,
    brand?.is_highlighted_brand_for_promotional_event
  );

  const handleFavoriteOnClick = () => {
    if (!brandTile?.brand?.token) {
      return;
    }

    const sourceId = signUpTracking?.sourceId
      ? `${signUpTracking?.sourceId}_`
      : "";

    if (!isLoggedInRetailer) {
      popSignUp(history, SignUpType.GENERIC, () => null, {
        ...signUpTracking,
        sourceId: `${sourceId}_brand_tile`,
      });
      return;
    }

    if (internalFavorite) {
      (unFavoriteBrand ?? unFollowBrand)(
        brandTile?.brand.token,
        retailerToken ?? ""
      );
    } else {
      (favoriteBrand ?? followBrand)(
        brandTile.brand.token,
        brandTile.brand.name ?? "",
        retailerToken ?? ""
      );
    }

    setInternalFavorite(!internalFavorite);
  };

  const handleAuxClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (event.button === 1) {
        onMiddleClick?.();
      } else if (event.button === 2) {
        onRightClick?.();
      }
    },
    [onMiddleClick, onRightClick]
  );

  return (
    <LeanBrandTileStyles.Wrapper
      className={className}
      data-test-id={isLoaded ? "maker-tile" : "maker-tile-placeholder"}
      onAuxClick={handleAuxClick}
      {...viewImpressionProps}
    >
      <LeanBrandTileStyles.Wrapper $border={!isLoggedInRetailer}>
        <FullWidthWrapper>
          {isLoaded ? (
            <>
              <BrandTileImageSection
                title={title}
                pageType={pageType}
                token={brand?.token ?? ""}
                images={carouselImages}
                linkLocation={link}
                name={brand?.name}
                eventPath="/non-treasure-hunt/maker-tile/fade-carousel"
                onLinkClick={onLinkClick}
              />
              {customButton && isLoaded ? (
                <ImageAndButtonContainer
                  $hasMultipleImages={carouselImages.length > 1}
                >
                  <CustomButtonContainer>{customButton}</CustomButtonContainer>
                </ImageAndButtonContainer>
              ) : null}
            </>
          ) : (
            <LoadingSkeleton width="100%" height="" aspectRatio="1" />
          )}
          <LeanBrandTileStyles.NameAndMore
            name={brand?.name}
            brand={brand}
            linkLocation={link}
            minimum_order_amount={brand?.minimum_order_amount}
            isFirstOrder={brand?.is_first_order ?? true}
            hasNewProducts={
              brand?.has_new_products && !isNewCategory(categoryUrlIdList?.[0])
            }
            promoText={brandTile?.promo_text ?? undefined}
            promoTextColor={promoText}
            promoTextBackground={
              transparentFeaturedPromoTextBackground
                ? "transparent"
                : lightBackground
            }
            token={brand?.token ?? ""}
            onLinkClick={onLinkClick}
            isLoggedIn={isLoggedInRetailer}
            brandPromoLabel={brandTile?.brand_promo_label}
            featuredBrandPromoLabel={brandTile?.featured_brand_promo_label}
            review={brandTile?.review}
            brandUrl={link}
            bottomPadding={bottomPadding}
            topPadding={topPadding}
            showMinimum={showMinimum}
            previousOrders={previousOrders}
          />
          {brandTile?.brand ? <BrandTileBadge tile={brandTile} /> : null}
        </FullWidthWrapper>
        {customButton && isLoaded ? <Spacer width="1x" /> : null}
        {isLoggedInRetailer && brand && !withoutFavourite ? (
          <FavoriteWrapper>
            <FavoriteButton
              ariaLabel={
                internalFavorite
                  ? strictLocalize(
                      {
                        id: "cq5oKD",
                        defaultMessage: "Un-follow {brandName}",
                        description: {
                          text: "Remove brand from favorites",
                        },
                      },
                      DEPRECATED_backwardsCompatibleValues({
                        brandName: brand.name,
                      })
                    )
                  : strictLocalize(
                      {
                        id: "1/hlmL",
                        defaultMessage: "Follow {brandName}",
                        description: {
                          text: "Add brand to favorites",
                        },
                      },
                      DEPRECATED_backwardsCompatibleValues({
                        brandName: brand.name,
                      })
                    )
              }
              onClick={handleFavoriteOnClick}
              favorited={internalFavorite}
            />
          </FavoriteWrapper>
        ) : null}
      </LeanBrandTileStyles.Wrapper>
    </LeanBrandTileStyles.Wrapper>
  );
};

const ImageAndButtonContainer = styled.div<{ $hasMultipleImages: boolean }>`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  padding-bottom: ${({ $hasMultipleImages }) =>
    $hasMultipleImages ? getSpacing("1x") : getSpacing(12)};
  bottom: ${({ $hasMultipleImages }) =>
    $hasMultipleImages ? getSpacing(12) : getSpacing("1x")};
`;

const CustomButtonContainer = styled.div`
  position: absolute;
  z-index: 1;
`;

const FavoriteWrapper = styled.div`
  z-index: 2;
  height: 0;
  position: absolute;
  top: 4px;
  right: 4px;
`;

const FullWidthWrapper = styled(Column)`
  height: 100%;
  width: 100%;
`;
